// Utils
import { formatString } from "@/utils/formatString";
import { enumToKeyName } from "@/utils/enumToKeyName";

// VueRouter
import { NavigationFailure, NavigationFailureType, isNavigationFailure } from "vue-router";

//
import { HiddenErrorName } from "@/models/ErrorHiddenFromConsole";
import { postSaveError } from "@/requests/error";

export class ErrorService {
  //* Functions to register handlers
  useWindowErrorHandler() {
    window.onerror = (event, source, lineno, colno, error) => {
      this.onWindowError(event, source, lineno, colno, error);
    };
  }

  useUnhandledRejection() {
    window.onunhandledrejection = (event: PromiseRejectionEvent) => {
      this.onUnhandledRejection(event);
    };
  }

  //* Functions for registered handlers
  onWindowError(event: string | Event, source?: string, lineno?: number, colno?: number, error?: Error) {
    const errorMessage = this.prepareWindowError(event, source, lineno, colno, error);
    this.sendError(errorMessage);
  }

  onUnhandledRejection(event: PromiseRejectionEvent) {
    const errorMessage = this.prepareUnhandledRejectionError(event);
    this.sendError(errorMessage);

    // TODO: should event be prevented by default?
    event.preventDefault();
  }

  onVueError(error: Error, vm: Vue, trace: string) {
    // Prevent send errors when "NextRequestStarted"
    if (error.name == HiddenErrorName) return;
    const errorMessage = this.prepareVueError(error, vm, trace);
    this.sendError(errorMessage);
  }

  onVueWarning(message: string, vm: Vue, trace: string) {
    if (message == "Failed to resolve directive: ripple") return;
    console.warn(`Warning: ${message}\nTrace: ${trace}`);
    console.warn(vm);
    // Default not send warnings to API
  }

  // eslint-disable-next-line
  onVueRouterError(error: any) {
    if (isNavigationFailure(error)) {
      const errorMessage = this.prepareVueRouterNavigationFailureError(error);
      this.showError(errorMessage);
    } else {
      const errorMessage = this.prepareVueRouterError(error);
      this.sendError(errorMessage);
    }
  }

  //* Private functions for formating errors
  private prepareWindowError(
    event: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ): string {
    return formatString(
      "WindowError => Message: '{0}', Source: '{1}', Lineno: '{2}', Colno: '{3}', Error: '{4}'",
      typeof event == "string" ? event : event.type,
      source,
      lineno,
      colno,
      error
    );
  }

  private prepareVueError(error: Error, vm: Vue, trace: string): string {
    return formatString("VueError => Error: '{0}', Trace: '{1}', Source: '{2}'", error, trace, vm.$el?.baseURI);
  }

  // eslint-disable-next-line
  private prepareVueRouterError(error: any) {
    return error; // TODO: hkopczacki - added formating error
  }

  private prepareVueRouterNavigationFailureError(error: NavigationFailure) {
    const _trace = this.prepareTrace(error.stack, 10);
    const _type = enumToKeyName(NavigationFailureType, error.type);

    return formatString(
      "VueRouterNavigationError => Error: '{0}', Type: '{1}', From: '{2}', To: '{3}', Trace: '{4}'",
      error.name,
      _type,
      error.from.fullPath,
      error.to.fullPath,
      _trace
    );
  }

  private prepareUnhandledRejectionError(event: PromiseRejectionEvent): string {
    const error = event.reason instanceof Error ? event.reason : event.reason;
    const trace = event.reason instanceof Error ? event.reason.stack : "Unknown trace";
    const source = event.target instanceof Window ? event.target.location.href : "Unknown source";

    const _trace = this.prepareTrace(trace, 5);

    return formatString("UnhandledRejectionError => Source: '{0}', Error: '{1}', Trace: '{2}'", source, error, _trace);
  }

  private prepareTrace(trace: string | undefined, itemsCount: number): string {
    return trace ? trace.split("\n").slice(0, itemsCount).join("\n") : "Unknown trace";
  }

  //* SendErrorRequest to API
  private async sendError(error: string) {
    await postSaveError(error);
    this.showError(error);
  }

  private showError(error: string) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(error);
    }
  }
}

const errorService = new ErrorService();
export default errorService;
